/**
 * BuyerReadinessHub — read-only summary view of a buyer's readiness profile.
 *
 * Default landing for the "Buyer Readiness" route. Shows completeness, key
 * financials, property preferences, and target locations. "Edit readiness"
 * swaps in the BuyerProfileWizard.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useBuyerProfile, useBuyerPreferences } from '../../agents/buyerProfileAgent';
import { PageHeader, SectionCard } from '../ui';
import BuyerProfileWizard from './BuyerProfileWizard';
import APIAgent from '../../agents/apiAgent';
import './BuyerReadinessHub.css';

const FINANCING_LABELS = {
  conventional: 'Conventional',
  fha: 'FHA',
  va: 'VA',
  cash: 'Cash',
  other: 'Other',
};
const CREDIT_LABELS = {
  excellent: 'Excellent (740+)',
  good: 'Good (670-739)',
  fair: 'Fair (580-669)',
  poor: 'Poor (<580)',
  unsure: 'Not sure',
};
const EMPLOYMENT_LABELS = {
  w2: 'W-2 Employee',
  self_employed: 'Self-employed',
  retired: 'Retired',
  other: 'Other',
};

const BuyerReadinessHub = ({ buyerId }) => {
  const [editing, setEditing] = useState(false);
  const [person, setPerson] = useState(null);
  const [editingPerson, setEditingPerson] = useState(false);
  const [maritalStatus, setMaritalStatus] = useState('');
  const [spouseEmail, setSpouseEmail] = useState('');
  const [spouseSearchResult, setSpouseSearchResult] = useState(null);
  const [spouseSearchError, setSpouseSearchError] = useState('');
  const [personSaving, setPersonSaving] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);

  const showFeedback = useCallback((message, type = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => setActionFeedback(null), 5000);
  }, []);
  const {
    profile,
    loading: profileLoading,
    error: profileError,
    fetchProfile,
  } = useBuyerProfile(buyerId);
  const {
    preferences,
    loading: prefsLoading,
    fetchPreferences,
  } = useBuyerPreferences(buyerId);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  // Load person record for marital status
  useEffect(() => {
    if (!buyerId) return;
    APIAgent.getPerson(buyerId).then((p) => {
      setPerson(p);
      setMaritalStatus(p.marital_status || '');
    }).catch(() => {});
  }, [buyerId]);

  const handleSearchSpouse = useCallback(async () => {
    setSpouseSearchError('');
    setSpouseSearchResult(null);
    if (!spouseEmail.trim()) return;
    try {
      const results = await APIAgent.searchPeopleByEmail(spouseEmail.trim());
      const match = Array.isArray(results) ? results[0] : null;
      if (!match) {
        setSpouseSearchError('No account found with that email.');
      } else if (match.id === buyerId) {
        setSpouseSearchError('Cannot link yourself as spouse.');
      } else {
        setSpouseSearchResult(match);
      }
    } catch {
      setSpouseSearchError('Search failed. Please try again.');
    }
  }, [spouseEmail, buyerId]);

  const handleSavePersonalInfo = useCallback(async () => {
    setPersonSaving(true);
    try {
      const payload = { marital_status: maritalStatus || null };
      if (maritalStatus === 'married' && spouseSearchResult) {
        payload.spouse_id = spouseSearchResult.id;
      }
      const updated = await APIAgent.updatePerson(buyerId, payload);
      setPerson(updated);
      setEditingPerson(false);
      setSpouseSearchResult(null);
      setSpouseEmail('');
      showFeedback('Personal info saved successfully!', 'success');
    } catch (err) {
      showFeedback(err.message || 'Failed to save personal info.', 'error');
    } finally {
      setPersonSaving(false);
    }
  }, [buyerId, maritalStatus, spouseSearchResult, showFeedback]);

  const completeness = useMemo(() => computeCompleteness(profile, preferences), [profile, preferences]);

  if (editing) {
    return (
      <div className="readiness-hub">
        <PageHeader
          eyebrow="Buying"
          title="Edit your readiness"
          subtitle="Update your financial picture, preferences, and target areas."
        />
        <BuyerProfileWizard
          buyerId={buyerId}
          onComplete={(message) => {
            setEditing(false);
            fetchProfile();
            fetchPreferences();
            showFeedback(message || 'Readiness saved successfully!', 'success');
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  const loading = profileLoading || prefsLoading;
  const hasAnyData = !!profile || !!preferences;

  return (
    <div className="readiness-hub">
      {actionFeedback && (
        <div className={`feedback-message ${actionFeedback.type}`} role="alert">
          {actionFeedback.message}
        </div>
      )}

      <PageHeader
        eyebrow="Buying"
        title="Buyer Readiness"
        subtitle="The single source of truth for what you can afford and what you want. Used to filter listings, score affordability, and pre-fill offers."
        actions={
          <button
            type="button"
            className="readiness-btn readiness-btn--primary"
            onClick={() => setEditing(true)}
          >
            {hasAnyData ? 'Edit readiness' : 'Get started'}
          </button>
        }
      />

      {profileError && (
        <SectionCard>
          <div className="readiness-error" role="alert">
            Couldn't load your profile: {profileError}
          </div>
        </SectionCard>
      )}

      {loading && !hasAnyData && (
        <SectionCard>
          <div className="readiness-empty">Loading your readiness profile…</div>
        </SectionCard>
      )}

      {!loading && !hasAnyData && (
        <SectionCard>
          <div className="readiness-empty-state">
            <h3>Set up your Buyer Readiness</h3>
            <p>
              Tell us your budget, financing path, must-haves, and preferred areas.
              We'll use it everywhere — search, alerts, affordability scoring, and offer drafts.
            </p>
            <button
              type="button"
              className="readiness-btn readiness-btn--primary"
              onClick={() => setEditing(true)}
            >
              Start setup
            </button>
          </div>
        </SectionCard>
      )}

      {hasAnyData && (
        <>
          <SectionCard
            title="Profile completeness"
            action={<button type="button" className="ui-section-card__action" onClick={() => setEditing(true)}>Improve</button>}
          >
            <div className="readiness-meter">
              <div className="readiness-meter__bar">
                <div className="readiness-meter__fill" style={{ width: `${completeness.percent}%` }} />
              </div>
              <div className="readiness-meter__label">
                <strong>{completeness.percent}%</strong> complete
                <span className="readiness-meter__sub">{completeness.filled} of {completeness.total} fields</span>
              </div>
            </div>
          </SectionCard>

          <div className="readiness-hub__grid">
            <SectionCard title="Financials">
              <SummaryList rows={[
                ['Budget ceiling', currency(profile?.budget_ceiling)],
                ['Pre-approval', currency(profile?.pre_approval_amount)],
                ['Down payment', profile?.down_payment_percent != null ? `${profile.down_payment_percent}%` : '—'],
                ['Max monthly', currency(profile?.max_monthly_payment)],
                ['Timeline', profile?.timeline_months ? `${profile.timeline_months} months` : '—'],
              ]} />
            </SectionCard>

            <SectionCard title="Readiness">
              <SummaryList rows={[
                ['Financing', FINANCING_LABELS[profile?.financing_type] || '—'],
                ['Credit', CREDIT_LABELS[profile?.credit_score_range] || '—'],
                ['Employment', EMPLOYMENT_LABELS[profile?.employment_status] || '—'],
                ['First-time buyer', profile?.first_time_buyer == null ? '—' : (profile.first_time_buyer ? 'Yes' : 'No')],
                ['Co-buyer', profile?.co_buyer_name || '—'],
              ]} />
            </SectionCard>

            <SectionCard title="Property wishlist">
              <SummaryList rows={[
                ['Bedrooms', range(preferences?.min_bedrooms, preferences?.max_bedrooms)],
                ['Bathrooms', range(preferences?.min_bathrooms, preferences?.max_bathrooms)],
                ['Property types', joinChips(preferences?.property_types)],
                ['Max HOA', preferences?.max_hoa_fee ? `$${preferences.max_hoa_fee}/mo` : '—'],
              ]} />
              <ChipList label="Must-haves" items={preferences?.must_haves} />
              <ChipList label="Nice-to-haves" items={preferences?.nice_to_haves} />
            </SectionCard>

            <SectionCard title="Target locations">
              {(preferences?.preferred_locations?.length ?? 0) === 0 ? (
                <div className="readiness-empty">No locations set yet.</div>
              ) : (
                <div className="chip-row">
                  {preferences.preferred_locations.map((loc) => (
                    <span key={loc} className="chip chip--selected">{loc}</span>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Personal info"
              action={
                !editingPerson
                  ? <button type="button" className="ui-section-card__action" onClick={() => setEditingPerson(true)}>Edit</button>
                  : null
              }
            >
              {!editingPerson ? (
                <SummaryList rows={[
                  ['Marital status', person?.marital_status ? person.marital_status.charAt(0).toUpperCase() + person.marital_status.slice(1) : '—'],
                  ['Spouse linked', person?.spouse_id ? `Person #${person.spouse_id}` : '—'],
                ]} />
              ) : (
                <div className="readiness-form">
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Marital status</label>
                    <select
                      value={maritalStatus}
                      onChange={(e) => setMaritalStatus(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
                    >
                      <option value="">— Not specified —</option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="widowed">Widowed</option>
                      <option value="divorced">Divorced</option>
                    </select>
                  </div>
                  {maritalStatus === 'married' && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Spouse's account email</label>
                      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                        Link your spouse's account for joint document signing (Wisconsin marital property law).
                      </p>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="email"
                          value={spouseEmail}
                          onChange={(e) => { setSpouseEmail(e.target.value); setSpouseSearchResult(null); setSpouseSearchError(''); }}
                          placeholder="spouse@example.com"
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
                        />
                        <button
                          type="button"
                          className="readiness-btn readiness-btn--secondary"
                          onClick={handleSearchSpouse}
                        >
                          Find
                        </button>
                      </div>
                      {spouseSearchError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{spouseSearchError}</p>}
                      {spouseSearchResult && (
                        <p style={{ color: '#16a34a', fontSize: 12, marginTop: 4 }}>
                          ✓ Found: {spouseSearchResult.first_name} {spouseSearchResult.last_name} — will be linked on save.
                        </p>
                      )}
                      {person?.spouse_id && !spouseSearchResult && (
                        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          Currently linked to Person #{person.spouse_id}.
                        </p>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="readiness-btn readiness-btn--primary"
                      onClick={handleSavePersonalInfo}
                      disabled={personSaving}
                    >
                      {personSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="readiness-btn readiness-btn--secondary"
                      onClick={() => { setEditingPerson(false); setMaritalStatus(person?.marital_status || ''); setSpouseEmail(''); setSpouseSearchResult(null); setSpouseSearchError(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
};

function SummaryList({ rows }) {
  return (
    <dl className="summary-list">
      {rows.map(([k, v]) => (
        <div className="summary-list__row" key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function ChipList({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="summary-chips">
      <div className="summary-chips__label">{label}</div>
      <div className="chip-row">
        {items.map((it) => (
          <span key={it} className="chip">{it}</span>
        ))}
      </div>
    </div>
  );
}

function currency(v) {
  if (v === null || v === undefined || v === '' || Number(v) <= 0) return '—';
  return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
function range(lo, hi) {
  if (lo == null && hi == null) return '—';
  if (lo != null && hi != null) return `${lo}–${hi}`;
  if (lo != null) return `${lo}+`;
  return `≤ ${hi}`;
}
function joinChips(arr) {
  return arr && arr.length ? arr.join(', ') : '—';
}

function computeCompleteness(profile, preferences) {
  const profileFields = [
    'budget_ceiling', 'pre_approval_amount', 'down_payment_percent',
    'max_monthly_payment', 'timeline_months', 'financing_type',
    'credit_score_range', 'employment_status', 'first_time_buyer', 'co_buyer_name',
  ];
  const prefFields = [
    'min_bedrooms', 'max_bedrooms', 'min_bathrooms', 'max_bathrooms',
    'property_types', 'must_haves', 'nice_to_haves',
    'preferred_locations', 'max_hoa_fee',
  ];
  let filled = 0;
  for (const f of profileFields) {
    const v = profile?.[f];
    if (v !== null && v !== undefined && v !== '' && v !== 0) filled += 1;
  }
  for (const f of prefFields) {
    const v = preferences?.[f];
    if (Array.isArray(v) ? v.length > 0 : (v !== null && v !== undefined && v !== '')) filled += 1;
  }
  const total = profileFields.length + prefFields.length;
  return { filled, total, percent: Math.round((filled / total) * 100) };
}

export default BuyerReadinessHub;
